import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import {
	InlinePreview,
	getPromptSummary,
	shouldShowInlinePreview,
} from '@/templates/paid-landing/sections/cfind-6215-frictionless-first-look-jira-v2/inline-preview';
import { jiraAiV2PromptHeadingStyles } from '@/templates/paid-landing/sections/cfind-6215-frictionless-first-look-jira-v2/jira-ai-v2-prompt-heading-styles';
import { messages as jiraAiV2Messages } from '@/templates/paid-landing/sections/cfind-6215-frictionless-first-look-jira-v2/messages';
import { paidJiraAiBackgroundV2SlideMs } from '@/templates/paid-landing/sections/cfind-6215-frictionless-first-look-jira-v2/paid-jira-ai-background-v2';
import { PaidJiraAiHeaderV2 } from '@/templates/paid-landing/sections/cfind-6215-frictionless-first-look-jira-v2/paid-jira-ai-header-v2';
import JiraSoftwareNav from '@/templates/software/jira/sections/nav-bar/software-jira-navbar';

import { Adg4SectionDivider } from '../../../components/adg4/section-divider/src/section-divider';
import { ADG4CtaWithPolygonBackground } from '../../../patterns/adg4/cta-with-polygon-background/cta-with-polygon-background';
import { GlobalFooter } from '../../../patterns/global-footer/src/global-footer';
import { PaidSocialProof } from '../../../patterns/paid-landings/paid-social-proof/src/paid-social-proof';
import { SimpleImageTextSide } from '../../../patterns/wpl/simple-image-text-side/src/simple-image-text-side';
import { experiments } from '../../../utils/experiment/manifest';
import { useExperimentExposure } from '../../../utils/experiment/use-experiment-exposure';
import { ParentScrollTracking } from '../../../utils/scroll-tracking/parent-scroll-tracker';
import { PaidJiraAiDisclaimer } from '../sections/cfind-4666-frictionless-first-look/paid-jira-ai-disclaimer';
import { PaidJiraAiTeamTypeMadLibPrompt } from '../sections/cfind-4666-frictionless-first-look/paid-jira-ai-team-type-mad-lib-prompt';
import { PaidAlternatingImageText } from '../sections/paid-alternating-image-text';
import { PaidCtaSection } from '../sections/paid-cta-section';
import { PaidCustomSection } from '../sections/paid-custom-section';
import { PaidTabsSection } from '../sections/paid-tabs-section';

import { JiraAiFFLProvider, useJiraAiFFL } from './hooks/use-jira-ai-ffl-context';
import { HorizontalRule, PaidLandingProps } from './paid-landing.template';

const promptContainerCss = css({
	width: '100%',
	maxWidth: '1152px',
	marginInline: 'auto',
	boxSizing: 'border-box',
	paddingInline: '24px',
	position: 'relative',
	textAlign: 'center',
	isolation: 'isolate',
});

const promptShellExitCss = css({
	opacity: 0,
	transform: 'translateY(-12px)',
	transition: `opacity ${paidJiraAiBackgroundV2SlideMs}ms ease, transform ${paidJiraAiBackgroundV2SlideMs}ms ease`,
	pointerEvents: 'none',
});

const promptContentCss = css({
	position: 'relative',
	zIndex: 1,
	width: '100%',
	maxWidth: '100%',
	marginInline: 'auto',
	opacity: 1,
	transform: 'translateY(0)',
	transition: `opacity ${paidJiraAiBackgroundV2SlideMs}ms ease, transform ${paidJiraAiBackgroundV2SlideMs}ms ease`,
});

const promptHeadingCss = css(jiraAiV2PromptHeadingStyles);

const promptContentExitCss = css({
	opacity: 0,
	transform: 'translateY(-10px)',
	pointerEvents: 'none',
});

const disclaimerExitCss = css({
	opacity: 0,
	transform: 'translateY(-8px)',
	transition: `opacity ${paidJiraAiBackgroundV2SlideMs}ms ease, transform ${paidJiraAiBackgroundV2SlideMs}ms ease`,
	pointerEvents: 'none',
});

interface PromptAndPreviewSectionProps {
	hideOriginalPrompt: boolean;
	isPromptExiting: boolean;
	showInlinePreview: boolean;
	cgsDataLoading: boolean;
	isErrorFromCgs: boolean;
	hasSubmittedDefaultValues: boolean;
	promptSummary: string;
	formattedCgsData: ReturnType<typeof useJiraAiFFL>['formattedCgsData'];
	spaceName?: string;
	onTryAnotherPrompt: () => void;
	promptRef: RefObject<HTMLDivElement>;
	inlinePreviewRef: RefObject<HTMLDivElement>;
}

const PromptAndPreviewSection = ({
	hideOriginalPrompt,
	isPromptExiting,
	showInlinePreview,
	cgsDataLoading,
	isErrorFromCgs,
	hasSubmittedDefaultValues,
	promptSummary,
	formattedCgsData,
	spaceName,
	onTryAnotherPrompt,
	promptRef,
	inlinePreviewRef,
}: PromptAndPreviewSectionProps) => {
	// Keep the prompt shell mounted during the exit animation so it can fade
	// out cleanly before the inline preview takes over.
	const showPromptShell = !hideOriginalPrompt || isPromptExiting;

	return (
		<>
			<div css={[promptContainerCss, isPromptExiting && promptShellExitCss]}>
				{showPromptShell && (
					<div css={[promptContentCss, isPromptExiting && promptContentExitCss]} ref={promptRef}>
						<h2 css={promptHeadingCss}>
							<FormattedMessage {...jiraAiV2Messages.promptBoxHeading} />
						</h2>
						<PaidJiraAiTeamTypeMadLibPrompt
							ctaMessage={jiraAiV2Messages.tryItOut}
							ctaKey="tryItOut"
						/>
					</div>
				)}
			</div>
			{showPromptShell && (
				<div css={[isPromptExiting && disclaimerExitCss]}>
					<PaidJiraAiDisclaimer />
				</div>
			)}
			{showInlinePreview && (
				<div ref={inlinePreviewRef}>
					<InlinePreview
						cgsDataLoading={cgsDataLoading}
						isErrorFromCgs={isErrorFromCgs}
						hasSubmittedDefaultValues={hasSubmittedDefaultValues}
						promptSummary={promptSummary}
						formattedCgsData={formattedCgsData}
						spaceName={spaceName}
						onTryAnotherPrompt={onTryAnotherPrompt}
					/>
				</div>
			)}
		</>
	);
};

interface LandingBodySectionsProps {
	tabsProps: PaidLandingProps['tabsProps'];
	customSection?: ReactNode;
	showHorizontalRule?: boolean;
	bannerProps?: PaidLandingProps['bannerProps'];
	resultSectionDivider?: PaidLandingProps['resultSectionDivider'];
	landingType: PaidLandingProps['landingType'];
	alternatingTextAndImageProps: PaidLandingProps['alternatingTextAndImageProps'];
	ctaProps?: PaidLandingProps['ctaProps'];
	ctaWithPolyGonProps?: PaidLandingProps['ctaWithPolyGonProps'];
	showStaticTestimonials: boolean;
	testimonialProps?: PaidLandingProps['testimonialProps'];
}

const LandingBodySections = ({
	tabsProps,
	customSection,
	showHorizontalRule,
	bannerProps,
	resultSectionDivider,
	landingType,
	alternatingTextAndImageProps,
	ctaProps,
	ctaWithPolyGonProps,
	showStaticTestimonials,
	testimonialProps,
}: LandingBodySectionsProps) => (
	<>
		<PaidTabsSection {...tabsProps} />
		{customSection && <PaidCustomSection customSection={customSection} />}
		{showHorizontalRule && <HorizontalRule />}
		{bannerProps && <SimpleImageTextSide {...bannerProps} />}
		{resultSectionDivider && (
			<div
				css={css({
					marginBlockEnd: '-80px',
				})}
			>
				<Adg4SectionDivider
					bgColor={resultSectionDivider.color}
					variation={resultSectionDivider.variation}
				/>
			</div>
		)}
		<PaidAlternatingImageText
			landingType={landingType}
			alternatingImageTextProps={alternatingTextAndImageProps}
		/>
		{ctaProps && <PaidCtaSection ctaProps={ctaProps} landingType={landingType} />}
		{ctaWithPolyGonProps && (
			<>
				<div
					css={
						resultSectionDivider &&
						css({
							backgroundColor: resultSectionDivider?.color,
							marginBlockStart: '-80px',
							height: '200px',
						})
					}
				/>
				<div
					css={
						resultSectionDivider &&
						css({
							marginBlockStart: '-200px',
						})
					}
				>
					<ADG4CtaWithPolygonBackground {...ctaWithPolyGonProps} />
				</div>
			</>
		)}
		{showStaticTestimonials && testimonialProps && <PaidSocialProof {...testimonialProps} />}
		<GlobalFooter />
	</>
);

export const PaidLandingJiraAiContent = ({
	landingType,
	showHorizontalRule,
	heroProps,
	bannerProps,
	tabsProps,
	customSection = undefined,
	alternatingTextAndImageProps,
	ctaProps,
	ctaWithPolyGonProps,
	testimonialProps,
	resultSectionDivider,
}: PaidLandingProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const promptRef = useRef<HTMLDivElement>(null);
	const inlinePreviewRef = useRef<HTMLDivElement>(null);
	const {
		cgsDataLoading,
		isErrorFromCgs,
		hasSubmittedDefaultValues,
		formattedCgsData,
		rawCgsData,
		workingOnValue,
		goalValue,
		workingOnSuggestion,
		goalSuggestion,
		firePaidJiraAi,
		promptAttemptIndex,
	} = useJiraAiFFL();
	const [jiraFflExposureFired, fireJiraFflExposure] = useExperimentExposure(
		experiments.cfind6469FrictionlessFirstLookJiraV3,
	);
	const [hideOriginalPrompt, setHideOriginalPrompt] = useState(false);
	const [isPromptExiting, setIsPromptExiting] = useState(false);

	const showStaticTestimonials = !heroProps.animatedSocialBanner;
	const promptSummary = useMemo(
		() =>
			getPromptSummary({
				workingOnValue,
				workingOnSuggestion,
				goalValue,
				goalSuggestion,
			}),
		[goalSuggestion, goalValue, workingOnSuggestion, workingOnValue],
	);
	const showInlinePreview =
		hideOriginalPrompt &&
		!isPromptExiting &&
		shouldShowInlinePreview({
			cgsDataLoading,
			formattedCgsData,
		});

	useEffect(() => {
		if (!jiraFflExposureFired) {
			fireJiraFflExposure();
		}
	}, [jiraFflExposureFired, fireJiraFflExposure]);

	useEffect(() => {
		if (!cgsDataLoading || hideOriginalPrompt) {
			return;
		}

		setIsPromptExiting(true);
		setHideOriginalPrompt(true);
		window.setTimeout(() => {
			setIsPromptExiting(false);
		}, paidJiraAiBackgroundV2SlideMs);
	}, [cgsDataLoading, hideOriginalPrompt]);

	useEffect(() => {
		if (!analyticsClient) {
			return; // be sure analyticsClient is available before firing event
		}
		createAnalyticsEvent({
			action: 'viewed',
			eventType: 'screen',
			name: 'PaidLandingJiraAiTemplate',
			attributes: {
				flow: 'paidLandingJiraAi',
			},
		}).fire();
	}, [analyticsClient, createAnalyticsEvent]);

	return (
		<main>
			<ParentScrollTracking pageId="paid-lp-jsw-ffl" scrollTrackingEnabled>
				<JiraSoftwareNav />
				<PaidJiraAiHeaderV2 />
				<PromptAndPreviewSection
					hideOriginalPrompt={hideOriginalPrompt}
					isPromptExiting={isPromptExiting}
					showInlinePreview={showInlinePreview}
					cgsDataLoading={cgsDataLoading}
					isErrorFromCgs={isErrorFromCgs}
					hasSubmittedDefaultValues={hasSubmittedDefaultValues}
					promptSummary={promptSummary}
					formattedCgsData={formattedCgsData}
					spaceName={rawCgsData?.sampleIssues.spaceName}
					promptRef={promptRef}
					inlinePreviewRef={inlinePreviewRef}
					onTryAnotherPrompt={() => {
						firePaidJiraAi('PreviewRetryClicked', 'clicked', {
							promptAttemptIndex,
							rowsRevealedCount: formattedCgsData?.length ?? 0,
						});
						setIsPromptExiting(false);
						setHideOriginalPrompt(false);
						window.requestAnimationFrame(() => {
							promptRef.current?.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});
						});
					}}
				/>
				<LandingBodySections
					tabsProps={tabsProps}
					customSection={customSection}
					showHorizontalRule={showHorizontalRule}
					bannerProps={bannerProps}
					resultSectionDivider={resultSectionDivider}
					landingType={landingType}
					alternatingTextAndImageProps={alternatingTextAndImageProps}
					ctaProps={ctaProps}
					ctaWithPolyGonProps={ctaWithPolyGonProps}
					showStaticTestimonials={showStaticTestimonials}
					testimonialProps={testimonialProps}
				/>
			</ParentScrollTracking>
		</main>
	);
};

export const PaidLandingJiraAiTemplate = (
	props: PaidLandingProps & { anchor?: string; subAnchor?: string },
) => (
	<JiraAiFFLProvider anchor={props.anchor} subAnchor={props.subAnchor}>
		<PaidLandingJiraAiContent {...props} />
	</JiraAiFFLProvider>
);
